import { message } from "antd";
import axios, { AxiosResponse, InternalAxiosRequestConfig } from "axios";
import { removeCurrentUser } from "@/utils/user";
import { API_URL } from "@/api/apiurl";
import { Decrypt, Encrypt } from "@/utils/crypto";

declare module "axios" {
    export interface AxiosRequestConfig {
        skipErrorHandler?: boolean;
    }
}

export const API_ERROR_MESSAGE_DURATION = 5;

export const getResponseErrorMessage = (error: unknown): string => {
    if (typeof error === "object" && error !== null && "msg" in error) {
        const msg = (error as ResponseData).msg;
        if (typeof msg === "string" && msg.trim()) {
            return msg.trim();
        }
    }
    if (error instanceof Error && error.message.trim()) {
        return error.message.trim();
    }
    return "";
};

// 使用 Vite 的环境变量
const baseURL = API_URL;

// 定义响应数据接口
interface ResponseData {
    code?: string;
    status?: string;
    msg?: string;
    [key: string]: any;
}

const allowUrls = ['checkLogin', 'checkPasswordLogin', 'sendSms', 'generateQrCode', 'qrCodeStatus', 'expired', 'allowLogin'];

const codeMessage: Record<number, string> = {
    200: "服务器成功返回请求的数据。",
    201: "新建或修改数据成功。",
    202: "一个请求已经进入后台排队（异步任务）。",
    204: "删除数据成功。",
    400: "发出的请求有错误，服务器没有进行新建或修改数据的操作。",
    401: "用户没有权限（令牌、用户名、密码错误）。",
    403: "用户得到授权，但是访问是被禁止的。",
    404: "发出的请求针对的是不存在的记录，服务器没有进行操作。",
    406: "请求的格式不可得。",
    410: "请求的资源被永久删除，且不会再得到的。",
    422: "当创建一个对象时，发生一个验证错误。",
    500: "服务器发生错误，请检查服务器。",
    502: "网关错误。",
    503: "服务不可用，服务器暂时过载或˝维护。",
    504: "网关超时。",
};

const SuccessCode = "10000";

const whiteInterface = ['/registration/exportRegistrationList', '/complaint/exportComplaintList', '/eLawyer/translate/export', '/feedback/export']

/**
 * 异常处理程序
 */
const errorHandler = (error: { response: ResponseData }) => {
    const { response } = error;
    if (response && response.status == "403") {
        removeCurrentUser();
        window.location.replace('/login');
        return;
    }
    let errText = "";
    if (response && response.status && response.msg) {
        const errorText = response.msg || codeMessage[Number(response.status)];
        errText = `${errorText}`;
    } else if (response && response.msg && response.code) {
        errText = `${response.code}:${response.msg}`;
        //accessId过期和账号多次登录
        if (response.status == "403") {
            removeCurrentUser();
            window.location.replace('/login');
        }
    } else if (response && response.msg) {
        errText = `${response.msg}`;
    } else if (response && response.statusText) {
        errText = `${response.statusText}`;
    } else if (response && response.code) {
        errText = `${response.code}`;
    } else if (!response) {
        errText = `网络异常`;
    }
    message.error(`${errText}`);
    return Promise.reject(response || "Error");
};

// 创建 axios 实例
const request = axios.create({
    baseURL,
    timeout: 300000,
});

// 请求拦截器
request.interceptors.request.use((config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem("token");
    const { responseType, notime, ...restData } = config.data || {};
    const url = config.url || "";

    // 确保URL正确处理
    if (url && !url.startsWith('/')) {
        config.url = `${url}`;
    }
    // 没有token的话并且url在允许直接访问的urls没有找到
    if (!token && allowUrls.every(item => url.indexOf(item) !== -1)) {
        removeCurrentUser();
        window.location.replace('/login');
        return Promise.reject(new Error("登录失效"));
    }
    config.headers.Authorization = token;

    //如果上传的是文件
    if (config.data instanceof FormData) {
        return config
    }
    // 处理 notime 选项
    if (!!responseType) {
        config.responseType = responseType
        config.data = restData.data;
        return config;
    }

    // 根据环境处理数据加密
    if (process.env.NODE_ENV === "production") {
        config.data = {
            requestData: Encrypt({
                ...config.data.data,
                timeStamp: new Date().getTime(),
            }),
        };
    } else {
        config.data = {
            ...config.data.data,
            timeStamp: new Date().getTime(),
        };
    }

    return config;
});

// 响应拦截器
request.interceptors.response.use(async (response: AxiosResponse) => {
    const { config, data, status } = response;
    const url = config.url || "";
    // 处理导出 Excel 的情况
    if (whiteInterface.some(i => url === i)) {
        if (status !== 200) {
            message.error("下载出错");
            return Promise.reject("下载出错");
        }
        return response.data;
    }

    // 处理普通响应数据
    let responseData = data;

    // 根据环境处理数据解密
    if (!(responseData.code || responseData.status)) {
        responseData = process.env.NODE_ENV === "production"
            ? JSON.parse(Decrypt(responseData))
            : responseData;
    }

    // 检查响应状态码
    if (
        responseData &&
        (responseData.code === SuccessCode || responseData.status === SuccessCode)
    ) {
        return responseData;
    } else {
        if (config.skipErrorHandler) {
            return Promise.reject(responseData);
        }
        return errorHandler({ response: responseData });
    }
}, (error) => {
    if (error?.config?.skipErrorHandler) {
        return Promise.reject(error.response?.data || error);
    }
    return errorHandler(error);
});

export default request;
